import isomorphicFetch from 'isomorphic-fetch';
import { NotificationManager } from 'react-notifications'; // https://www.npmjs.com/package/react-notifications
import UserNotLoggedInException from '../UserNotLoggedInException';
import BadRequestException from '../BadRequestException';
import TrevorQueryFailedException from '../TrevorQueryFailedException';
import ProgressChecklist_LEGACY from '../../components/ProgressChecklist_LEGACY';
import UserActionsHandler from '../../containers/UserActionsHandler';

const X_CSRF_TOKEN_HEADER = 'x-csrf-token'; // see TrevorWebSecurityConfigurerAdapter
const CSRF_TOKEN_SELECTOR = 'meta[name="csrf-token"]';
const X_REQUESTED_WITH = 'x-requested-with'; // see LoginController

const ajaxErrorHandler = (title, body) => {
  // shows a flash message
  NotificationManager.error(body, title, 10 * 1000);
};

// TODO: this this a temporary solution until we completely migrate to RTK-query.
const PATHS_TO_PREFIX_WITH_API_REGEXPS = [
  /^\/datasources\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/views\/import\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /^\/datasources\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/dashboards$/i,
  /^\/datasources\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/dashboards\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /^\/datasources\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/rows\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
];
const prefixPath = (fullPath) => {
  if (PATHS_TO_PREFIX_WITH_API_REGEXPS.some((regex) => regex.test(fullPath))) {
    return `/api/v1${fullPath}`;
  }
  return fullPath;
};

function ajax(desc, path, payload = {}, attempt = 1, isAbsolute = false) {
  const request = {
    ...payload,
    credentials: 'same-origin' /* force cookies to be sent */,
    cache: 'no-store' /* needed to skip iOS' cache */,
  };
  const fetchPath = prefixPath(
    isAbsolute ? path : datasourceUrlPrefix() + path,
  );
  return isomorphicFetch(fetchPath, request)
    .then((response) => {
      if (response.status >= 200 && response.status < 300) {
        // success
        return response.json();
      }
      if (
        response.status === 403 &&
        response.headers.get(X_CSRF_TOKEN_HEADER) &&
        attempt === 1
      ) {
        // CSRF token has expired, so update it and try again
        $(CSRF_TOKEN_SELECTOR).attr(
          'value',
          response.headers.get(X_CSRF_TOKEN_HEADER),
        );
        payload.headers['x-csrf-token'] = csrfToken();
        return ajax(desc, path, payload, attempt + 1);
      }
      if (response.status === 401) {
        throw new UserNotLoggedInException();
      } else if (response.status === 400) {
        return response.json().then((json) => {
          throw new TrevorQueryFailedException(
            json._reason,
            json._msg,
            json._layerId,
          );
        });
      } else {
        const error = new Error(response.statusText);
        error.response = response;
        throw error;
      }
    })
    .catch((e) => {
      // NOTE: catch only happens if there is a network error, or if above 'then' block throws an error
      window.console.error(`problem requesting: ${path}`, e);
      if (attempt > 1) {
        // only handle exception in the first attempt (otherwise error messages get shown twice)
        throw e;
      }
      if (desc) {
        if (e instanceof TrevorQueryFailedException) {
          ajaxErrorHandler(`Problem ${desc}`, e.msg());
        } else if (e instanceof UserNotLoggedInException) {
          ajaxErrorHandler(
            `Problem ${desc}`,
            "You've been logged out. Refresh the page and log back in",
          );
        } else {
          ajaxErrorHandler(
            `Oops! Problem ${desc}`,
            'Refresh the page or ping us for help',
          );
        }
      }
      throw e;
    });
}

export function get(desc, path, isAbsolute = false) {
  return ajax(desc, path, null, 0, isAbsolute);
}

export function postForm(desc, path, payload = {}) {
  const formData = new FormData();
  for (const key in payload) {
    const value = payload[key];
    formData.append(key, value);
  }
  return ajax(desc, path, {
    method: 'POST',
    body: formData,
    headers: {
      'x-csrf-token': csrfToken(),
      'x-requested-with': 'XMLHttpRequest',
    },
  });
}

export function postDelete(desc, path, payload = {}) {
  return post(desc, path, payload, 'DELETE');
}

export function post(desc, path, payload = {}, method = 'POST') {
  return ajax(desc, path, {
    method,
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      'x-csrf-token': csrfToken(),
      'x-requested-with': 'XMLHttpRequest',
    },
  });
}

export function postPatch(desc, path, payload = {}) {
  return post(desc, path, payload, 'PATCH');
}

export function csrfToken() {
  return $(CSRF_TOKEN_SELECTOR).attr('value');
}

export function datasourceUrlPrefix() {
  const prefix = getUrlPaths();
  return prefix ? `/datasources/${prefix[2]}` : '';
}

export function getUrlPaths() {
  return window.location.pathname.match(
    /^\/(publicdatasets|datasources)\/([a-z0-9-]+)/,
  );
}

export function getRowsRequestNewTabUrl(rowsRequest) {
  const prefix = getUrlPaths();
  return `/${prefix[1]}/${prefix[2]}/row-request/${rowsRequest}/edit`;
}

export function datasourceId() {
  return window.location.pathname.match(/^\/datasources\/([a-z0-9-]*)/)[1];
}

export function userAction(action, meta = {}, pingInstantly) {
  postJson('/actions', {
    _actions: [
      {
        _actionAt: new Date().toISOString(),
        _action: action,
        _meta: meta,
      },
    ],
  });
  setTimeout(() => {
    // use setTimeout to ensure that these actions are not fired while redux thread is running
    ProgressChecklist_LEGACY.userActionFired(action);
    UserActionsHandler.userActionFired(action);
  }, 1);
  try {
    Intercom('trackEvent', action, meta);
    if (pingInstantly) {
      setTimeout(function () {
        const ts = Math.round(new Date().getTime() / 1000);
        Intercom('update', { dummy_attribute: ts });
      }, 3000);
    }
  } catch (e) {
    window.console.error(e);
  }
}

//
// DEPRECATED fetch commands are below
//

function fetch(path, additionalFields, attempt = 1) {
  const fullPath = datasourceUrlPrefix() + path;
  return fetchFullPath(fullPath, additionalFields, attempt);
}

/**
 Fetch function that handles caveats: https://github.com/github/fetch#caveats
 */
function fetchFullPath(_fullPath, additionalFields = {}, attempt = 1) {
  const fullPath = prefixPath(_fullPath);

  const params = {
    credentials: 'same-origin', // send cookies in headers
    headers: {}, // create empty object, just in case additionalFields doesn't provide it (we need it for CSRF token below)

    ...additionalFields,
  };
  // add CSRF token to header
  params.headers[X_CSRF_TOKEN_HEADER] = $(CSRF_TOKEN_SELECTOR).attr('value');
  params.headers[X_REQUESTED_WITH] = 'XMLHttpRequest';
  // perform ajax request
  return isomorphicFetch(fullPath, params).then(function (response) {
    if (response.status >= 200 && response.status < 300) {
      return response;
    }
    if (
      response.status === 403 &&
      response.headers.get(X_CSRF_TOKEN_HEADER) &&
      attempt === 1
    ) {
      // CSRF token has expired, so update it and try again
      $(CSRF_TOKEN_SELECTOR).attr(
        'value',
        response.headers.get(X_CSRF_TOKEN_HEADER),
      );
      return fetchFullPath(fullPath, additionalFields, attempt + 1);
    }
    if (response.status === 401) {
      throw new UserNotLoggedInException();
    } else if (response.status === 400) {
      throw new BadRequestException(response);
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      throw error;
    }
  });
}

export function fetchJson(path, additionalFields = {}) {
  return fetch(path, additionalFields).then(function (data) {
    return data.json();
  });
}

export function postJson(path, json, method = 'POST') {
  const headers = {};
  headers['Content-Type'] = 'application/json;charset=UTF-8';
  return fetchJson(path, {
    method,
    body: JSON.stringify(json),
    headers,
  });
}

export function postFormData(path, formData) {
  return fetchJson(path, {
    method: 'POST',
    body: formData,
  });
}

/**
 * POSTs to a given path (different to other fetch functions, in that it won't be prefixed by window.location.pathname)
 */
export function postAbsolutePath(fullPath, json = null) {
  if (json) {
    return fetchFullPath(fullPath, {
      method: 'POST',
      body: JSON.stringify(json),
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
    }).then(function (data) {
      return data.json();
    });
  }
  return fetchFullPath(fullPath, {
    method: 'POST',
  });
}

/**
 * GETs a given path (different to other fetch functions, in that it won't be prefixed by window.location.pathname)
 */
export function fetchAbsolutePath(fullPath, jsonResponse = false) {
  if (jsonResponse) {
    return fetchFullPath(fullPath).then(function (data) {
      return data.json();
    });
  }
  return fetchFullPath(fullPath);
}

export function ajaxDelete(path) {
  return postJson(path, {}, 'DELETE');
}

export function postErrorToServer(stackTrace) {
  return postJson('/clientError', {
    _stack: stackTrace,
    _href: window.location.href,
  });
}
